import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import { Engine } from '@/core/Engine';
import { ISO_YAW } from '@/core/IsoCamera';
import { CHARACTER_LIGHT, createCharacterLight, createNightLighting, LightPool, updateNightLighting } from '@/fx/lighting';
import { Sfx } from '@/audio/Sfx';
import { CollisionWorld } from '@/world/collision';
import { bodies } from '@/world/bodies';
import { animateLevelWater } from '@/world/LevelLoader';
import { HARBOR } from '@/world/districts/harbor';
import { WORKERS } from '@/world/districts/workers';
import { CITY_CHUNKS, chunkAt } from '@/world/CityMap';
import { WorldStreamer } from '@/world/WorldStreamer';
import { Player } from '@/gameplay/Player';
import { GameState } from '@/gameplay/GameState';
import { VisualSettings } from '@/gameplay/VisualSettings';
import { Combat } from '@/gameplay/Combat';
import { cameraContext } from '@/gameplay/CameraContext';
import { ChaseSystem } from '@/gameplay/Chase';
import { CutscenePlayer, type CutsceneSequence } from '@/gameplay/Cutscene';
import { DialogueRunner, type DialogueDefinition } from '@/gameplay/Dialogue';
import { Enemy, type EnemyConfig } from '@/gameplay/Enemy';
import { Follower, type FollowerConfig } from '@/gameplay/Follower';
import { missionCompletedFlag, SurvivorsMission } from '@/gameplay/Mission';
import { createMissionRegistry } from '@/gameplay/MissionRegistry';
import { STORY_MISSIONS } from '@/gameplay/missions';
import { PROLOGUE_MISSION_ID, PrologueMission } from '@/gameplay/missions/prologue';
import { SURVIVORS_MISSION_ID } from '@/gameplay/missions/survivors';
import {
  DE_BEGRAFENIS_AMBUSH,
  DE_BEGRAFENIS_MISSION_ID,
  DeBegrafenisMission,
} from '@/gameplay/missions/deBegrafenis';
import { SafehouseSystem } from '@/gameplay/Safehouse';
import { TrafficController } from '@/gameplay/Traffic';
import { OcclusionSystem } from '@/fx/OcclusionSystem';
import { Feedback } from '@/fx/Feedback';
import { loadCharacterRig } from '@/meshkit/character/loadRig';
import { LUCA, LUCA_MODEL_URL } from '@/meshkit/character/spec';
import { DebugOverlay } from '@/ui/debug';
import { Hud } from '@/ui/hud';
import { ObjectiveMarker } from '@/ui/ObjectiveMarker';
import { CutsceneOverlay } from '@/ui/cutscene';
import { SettingsPanel } from '@/ui/settings';
import { DialoguePanel } from '@/ui/dialogue';

/**
 * Bootstrap: set up the engine, load the harbor district, place Luca, and start the loop.
 */

const container = document.getElementById('app');
if (!container) throw new Error('#app ontbreekt in index.html');

const debugEnabled = new URLSearchParams(window.location.search).has('debug');
const collision = new CollisionWorld();

// Long-lived state comes before the world: a save will eventually decide where Luca starts
// and what he owns. Without one, the game continues from the default start state.
const state = new GameState();
const loadedSave = state.load();
const visualSettings = new VisualSettings();
const missionRegistry = createMissionRegistry(STORY_MISSIONS);

// Autosave writes after a short pause, so a closing tab can miss the last change. `pagehide`
// also fires on mobile, where `beforeunload` often does not.
window.addEventListener('pagehide', () => state.save());

let isDriving = false;
let lastVehicleDamage: unknown = null;
let currentCameraContext = cameraContext({ isDriving: false, isAiming: false, velocity: new THREE.Vector2() });
let debugCameraViewHeight: number | null = null;
bus.on('vehicle:damaged', (event) => { lastVehicleDamage = event; });

const engine = new Engine(container, {
  update(dt) {
    engine.input.updateAim(engine.camera);
    if (dialogue.active) {
      updateDialogueInput();
    } else if (feedback.frozen) {
      // Hitstop: de simulatie staat even stil, het beeld niet. Camera, HUD en de invoerstap
      // hieronder lopen gewoon door, anders zou de klap als een hapering lezen.
    } else if (cutscene.active) {
      cutscene.update(dt);
      if (engine.input.wasPressed('escape')) cutscene.skip();
    } else {
      const drivingVehicle = vehicles.find((vehicle) => vehicle.occupied);
      isDriving = drivingVehicle !== undefined;
      if (drivingVehicle) {
        drivingVehicle.update(dt, engine.input, collision, player, vehicles);
      } else {
        player.update(dt, engine.input, collision, ISO_YAW, combat);
      }
      worldStreamer.update(player.position, drivingVehicle?.velocity ?? player.velocity);
      traffic.update(dt, engine.input, collision, player);
      for (const vehicle of vehicles) {
        if (vehicle === drivingVehicle) continue;
        vehicle.update(dt, engine.input, collision, player, vehicles);
      }
      for (const enemy of enemies) enemy.update(dt, player, collision, combat);
      chase.update(dt, vehicles);
      for (const follower of followers) {
        follower.update(dt, {
          leader: player,
          followers,
          safehouse: HARBOR.safehouse,
          world: collision,
          enemies,
          combat,
        });
      }
      mission?.update();
      safehouses.update(engine.input);
    }
    combat.update(dt);
    sfx.update(vehicles.find((vehicle) => vehicle.occupied)?.speed ?? 0);
    animateLevelWater(engine.scene, engine.loop.elapsed);
    objectiveMarker.update(dt, player.position);
    hud.update(dt);
    cutsceneOverlay.update();
    dialoguePanel.update();
    characterLight.position.set(player.position.x, CHARACTER_LIGHT.height, player.position.y);
    currentCameraContext = cameraContext({
      isDriving,
      isAiming: player.aiming,
      velocity: currentCameraVelocity(),
    });
    const targetViewHeight = debugCameraViewHeight ?? currentCameraContext.viewHeight;
    engine.isoCamera.viewHeight = THREE.MathUtils.lerp(engine.isoCamera.viewHeight, targetViewHeight, Math.min(1, dt * 3.4));
    if (!cutscene.active) engine.isoCamera.follow(focusPoint(), dt, {
      response: currentCameraContext.followResponse,
      deadZone: currentCameraContext.deadZone,
    });
    occlusion.update(dt);
    // Verzorgt ook `isoCamera.update(dt)`, dus het schudden verloopt ook tijdens een cutscene
    // en tijdens de hitstop zelf.
    feedback.update(dt);
    engine.input.endStep();
  },
  render() {
    debug?.beginFrame();
    updateNightLighting(night, engine.isoCamera.focus);
    lights.update(engine.isoCamera.focus);
    engine.render();
    debug?.endFrame();
  },
});

const lights = new LightPool(engine.scene);
const night = createNightLighting(engine.scene);
const characterLight = createCharacterLight(engine.scene);
visualSettings.apply({ scene: engine.scene, night, characterLight, renderer: engine.renderer });
const sfx = new Sfx();

// Luca is the one Blender-driven character: his clothes and face do not read well as simple
// primitives. If loading fails, the game falls back to the procedural rig so the harbor still
// starts.
const lucaRig = await loadCharacterRig(LUCA_MODEL_URL, LUCA).catch((error: unknown) => {
  console.error('HD-model van Luca kon niet laden; terug naar de procedurele rig.', error);
  return undefined;
});
const lucaRigSource = lucaRig ? 'HD GLB' : 'procedurele fallback';
console.info(`Luca rig: ${lucaRigSource}`);
showLucaRigSource(lucaRigSource);

const combat = new Combat(engine.scene, collision);
let currentCheckpoint = HARBOR.playerSpawn.clone();

const player = new Player(HARBOR.playerSpawn.clone(), lucaRig);
engine.scene.add(player.object);
// Luca is also in the target list: enemies start shooting back in NA-03.
combat.addTarget(player);
// Ook in het botsingsregister: hij moet tegen auto's en mensen aan lopen, en onder een
// bumper kunnen belanden.
bodies.addActor(player);
const chase = new ChaseSystem(player, collision);
const cutscene = new CutscenePlayer(engine.isoCamera);
const dialogue = new DialogueRunner(state);
const occlusion = new OcclusionSystem(engine.scene, engine.camera, player);
const feedback = new Feedback(engine.scene, engine.isoCamera);
const safehouses = new SafehouseSystem(player, [
  {
    id: 'workers',
    name: 'Workers Safehouse',
    position: WORKERS.safehouse,
    radius: WORKERS.safehouseRadius,
  },
]);

const enemyConfigs: EnemyConfig[] = [
  {
    id: 1,
    patrol: [new THREE.Vector2(14, -4), new THREE.Vector2(28, -4)],
  },
  {
    id: 2,
    patrol: [new THREE.Vector2(-10, 3), new THREE.Vector2(-26, 3)],
  },
  {
    id: 3,
    patrol: [new THREE.Vector2(-38, -4), new THREE.Vector2(-26, -4)],
  },
];
const enemies: Enemy[] = [];

const followerConfigs: FollowerConfig[] = [
  { id: 1, spawn: new THREE.Vector2(29.5, 2.2), slot: 0 },
  { id: 2, spawn: new THREE.Vector2(32.2, 2.6), slot: 1 },
  { id: 3, spawn: new THREE.Vector2(34.7, 1.9), slot: 2 },
];
const followers: Follower[] = [];
const traffic = new TrafficController({ routes: [], parkedVehicles: [] }, engine.scene, bodies);
const worldStreamer = new WorldStreamer({ scene: engine.scene, collision, lights, traffic, occlusion });
worldStreamer.loadInitial(HARBOR.playerSpawn);
const vehicles = traffic.vehicles;
let mission: SurvivorsMission | null = null;
let prologue: PrologueMission | null = null;
let deBegrafenis: DeBegrafenisMission | null = null;
const objectiveMarker = new ObjectiveMarker(WORKERS.safehouse);
engine.scene.add(objectiveMarker.object);

bus.on('mission:completed', ({ id }) => {
  if (id === SURVIVORS_MISSION_ID) startDeBegrafenis();
});

spawnActors();
traffic.addToScene(engine.scene);

const ui = document.getElementById('ui');
if (!ui) throw new Error('#ui ontbreekt in index.html');
const hud = new Hud(ui, {
  player,
  enemies,
  chase,
  districtName: currentDistrictName,
  objectiveDistance: objectiveDistance,
  navigation: navigationSnapshot,
  onRestartFromCheckpoint: restartFromCheckpoint,
});

const lightingTargets = { scene: engine.scene, night, characterLight, renderer: engine.renderer };
const settingsPanel = new SettingsPanel(ui, {
  initial: visualSettings.toJSON(),
  onChange(key, value) {
    if (key === 'brightness') visualSettings.setBrightness(value);
    else if (key === 'ambient') visualSettings.setAmbient(value);
    else visualSettings.setCharacterLight(value);
    visualSettings.apply(lightingTargets);
  },
  onReset() {
    visualSettings.reset();
    visualSettings.apply(lightingTargets);
    return visualSettings.toJSON();
  },
  onToggleMute: () => sfx.toggleMute(),
});
const cutsceneOverlay = new CutsceneOverlay(ui, cutscene);
const dialoguePanel = new DialoguePanel(ui, dialogue);

startOpeningMission();

const focus = new THREE.Vector3();
function focusPoint(): THREE.Vector3 {
  return focus.set(
    player.position.x + currentCameraContext.lookAhead.x,
    0,
    player.position.y + currentCameraContext.lookAhead.y,
  );
}

function currentDistrictName(): string {
  const chunk = chunkAt(player.position);
  return chunk?.levelData.semantics?.districtName ?? chunk?.district ?? 'New Amsterdam';
}

function objectiveDistance(): number | null {
  return player.position.distanceTo(WORKERS.safehouse);
}

function navigationSnapshot() {
  const occupiedVehicle = vehicles.find((vehicle) => vehicle.occupied);
  const heading = occupiedVehicle?.rotation ?? player.object.rotation.y;
  const activeChunkIds = new Set(worldStreamer.activeChunkIds);
  const distance = player.position.distanceTo(WORKERS.safehouse);
  return {
    district: currentDistrictName(),
    chunks: CITY_CHUNKS.map((chunk) => ({
      id: chunk.id,
      districtName: chunk.levelData.semantics?.districtName ?? chunk.district,
      x: chunk.bounds.x,
      z: chunk.bounds.z,
      halfX: chunk.bounds.halfX,
      halfZ: chunk.bounds.halfZ,
      active: activeChunkIds.has(chunk.id),
    })),
    player: {
      x: player.position.x,
      z: player.position.y,
      heading,
    },
    objective: {
      x: WORKERS.safehouse.x,
      z: WORKERS.safehouse.y,
      label: 'SAFEHOUSE',
      distance,
    },
  };
}

function currentCameraVelocity(): THREE.Vector2 {
  return vehicles.find((vehicle) => vehicle.occupied)?.velocity ?? player.velocity;
}

engine.isoCamera.snapTo(focusPoint());

const debug = debugEnabled
  ? new DebugOverlay(
    engine,
    () => lights.activeCount,
    () => collision.obstacleCount,
    () => worldStreamer.stats(),
    { onCameraZoomOverride: (viewHeight) => { debugCameraViewHeight = viewHeight; } },
  )
  : null;

declare global {
  interface Window {
    __naDebug?: {
      engine: Engine;
      worldStreamer: WorldStreamer;
      collision: CollisionWorld;
      lights: LightPool;
      stats(): {
        renderer: ReturnType<Engine['stats']>;
        world: ReturnType<WorldStreamer['stats']>;
        lights: number;
        obstacles: number;
      };
    };
  }
}

window.__naDebug = {
  engine,
  worldStreamer,
  collision,
  lights,
  stats: () => ({
    renderer: engine.stats(),
    world: worldStreamer.stats(),
    lights: lights.activeCount,
    obstacles: collision.obstacleCount,
  }),
};

engine.loop.start();

console.info(
  loadedSave
    ? `Save geladen: $${state.money}, ${state.ownedDistricts().length} wijk(en) in bezit.`
    : 'Geen save gevonden; nieuw spel.',
);

// Useful while developing: expose the world from the console.
Object.assign(window as unknown as Record<string, unknown>, {
  engine,
  player,
  collision,
  lights,
  bus,
  lucaRigSource,
  state,
  combat,
  enemies,
  followers,
  hud,
  get mission() { return mission; },
  get prologue() { return prologue; },
  occlusion,
  feedback,
  chase,
  sfx,
  visualSettings,
  settingsPanel,
  safehouses,
  vehicles,
  traffic,
  worldStreamer,
  objectiveMarker,
  cameraContext: () => currentCameraContext,
  lastVehicleDamage: () => lastVehicleDamage,
  cutscene,
  playPrologue: () => startPrologue(),
  playDebugCutscene,
  dialogue,
  playLieutenantDialogue,
});

function spawnActors(): void {
  enemies.length = 0;
  for (const config of enemyConfigs) {
    const enemy = new Enemy(config);
    enemies.push(enemy);
    engine.scene.add(enemy.object);
    combat.addTarget(enemy);
    bodies.addActor(enemy);
    chase.registerEnemy(enemy);
  }

  followers.length = 0;
  for (const config of followerConfigs) {
    const follower = new Follower(config);
    followers.push(follower);
    engine.scene.add(follower.object);
    combat.addTarget(follower);
    bodies.addActor(follower);
  }
}

function clearActors(): void {
  for (const enemy of enemies) {
    combat.removeTarget(enemy);
    chase.removeEnemy(enemy);
    enemy.dispose();
  }
  enemies.length = 0;

  for (const follower of followers) {
    combat.removeTarget(follower);
    follower.dispose();
  }
  followers.length = 0;
}

function createMission(): SurvivorsMission {
  return new SurvivorsMission({
    definition: missionRegistry.get('na-01-overlevenden'),
    state,
    player,
    followers,
    safehouse: HARBOR.safehouse,
  });
}

function startDeBegrafenis(): void {
  mission?.dispose();
  mission = null;
  deBegrafenis?.dispose();
  currentCheckpoint = WORKERS.entry.clone();
  deBegrafenis = new DeBegrafenisMission({
    definition: missionRegistry.get(DE_BEGRAFENIS_MISSION_ID),
    state,
    cutscene,
    onAmbushStart: spawnAmbush,
    onComplete: () => {
      deBegrafenis = null;
    },
  });
  (window as unknown as Record<string, unknown>).mission = deBegrafenis;
  deBegrafenis.start();
}

function spawnAmbush(): void {
  for (const config of DE_BEGRAFENIS_AMBUSH) {
    const enemy = new Enemy(config);
    enemies.push(enemy);
    engine.scene.add(enemy.object);
    combat.addTarget(enemy);
    bodies.addActor(enemy);
    chase.registerEnemy(enemy);
  }
}

function startOpeningMission(): void {
  const shouldPlayPrologue = !loadedSave && !state.flag(missionCompletedFlag(PROLOGUE_MISSION_ID));
  if (shouldPlayPrologue) {
    startPrologue();
    return;
  }
  mission = createMission();
}

function startPrologue(): void {
  mission?.dispose();
  mission = null;
  prologue?.dispose();
  prologue = new PrologueMission({
    definition: missionRegistry.get(PROLOGUE_MISSION_ID),
    state,
    cutscene,
    scene: engine.scene,
    playerObject: player.object,
    onComplete: () => {
      prologue = null;
      mission = createMission();
      (window as unknown as Record<string, unknown>).mission = mission;
    },
  });
  (window as unknown as Record<string, unknown>).mission = mission;
  prologue.start();
}

function restartFromCheckpoint(): void {
  const resumeDeBegrafenis = deBegrafenis !== null;
  prologue?.dispose();
  prologue = null;
  mission?.dispose();
  mission = null;
  deBegrafenis?.dispose();
  deBegrafenis = null;
  clearActors();
  player.resetTo(currentCheckpoint);
  spawnActors();
  if (resumeDeBegrafenis) {
    startDeBegrafenis();
  } else {
    mission = createMission();
    (window as unknown as Record<string, unknown>).mission = mission;
  }
  engine.isoCamera.snapTo(focusPoint());
}

function showLucaRigSource(source: string): void {
  if (!debugEnabled) return;
  const uiRoot = document.getElementById('ui');
  if (!uiRoot) return;
  const badge = document.createElement('div');
  badge.textContent = `Luca: ${source}`;
  Object.assign(badge.style, {
    position: 'absolute',
    left: '12px',
    bottom: '12px',
    padding: '6px 8px',
    border: '1px solid rgba(232, 221, 200, 0.35)',
    background: 'rgba(8, 8, 10, 0.72)',
    color: '#e8ddc8',
    font: '12px ui-monospace, SFMono-Regular, Menlo, monospace',
  });
  uiRoot.appendChild(badge);
}

function debugCutscene(): CutsceneSequence {
  return {
    id: 'debug-harbor-intro',
    beats: [
      {
        duration: 1.4,
        camera: { focus: new THREE.Vector3(HARBOR.safehouse.x, 0, HARBOR.safehouse.y), viewHeight: 28, ease: 'smooth' },
        text: { speaker: 'Rocco', line: 'Safehouse is daar. Blijf laag, Luca.' },
      },
      {
        duration: 1.4,
        camera: { focus: new THREE.Vector3(HARBOR.quayZ, 0, HARBOR.quayZ), viewHeight: 32, ease: 'smooth' },
        text: { speaker: 'Luca', line: 'Eerst de haven door. Daarna praten we over veilig.' },
      },
      {
        duration: 1.0,
        camera: { focus: focusPoint().clone(), viewHeight: engine.isoCamera.viewHeight, ease: 'smooth' },
      },
    ],
  };
}

function playDebugCutscene(): void {
  cutscene.play(debugCutscene());
}

function updateDialogueInput(): void {
  if (engine.input.wasPressed('enter')) dialoguePanel.handleKey('enter');
  for (let index = 1; index <= 9; index++) {
    if (engine.input.wasPressed(String(index))) dialoguePanel.handleKey(String(index));
  }
}

function lieutenantDialogue(): DialogueDefinition {
  return {
    id: 'lieutenants-first-table',
    start: 'rocco',
    nodes: [
      {
        id: 'rocco',
        speaker: 'Rocco',
        text: 'We got men scared, streets hot, and one room nobody has burned down yet.',
        next: 'bruno',
      },
      {
        id: 'bruno',
        speaker: 'Bruno',
        text: 'Pick the first signal carefully. Money, loyalty, or fear. The city hears all three.',
        choices: [
          {
            label: 'Keep the people close',
            next: 'dante',
            effects: [{ reputation: { populace: 4 }, relationship: { rocco: 5 }, flag: 'first_table_people' }],
          },
          {
            label: 'Make Barili nervous',
            next: 'dante',
            effects: [{ reputation: { rivals: 5 }, relationship: { dante: 5 }, flag: 'first_table_rivals' }],
          },
          {
            label: 'Hold cash for the family',
            next: 'dante',
            effects: [{ money: 150, relationship: { bruno: 5 }, flag: 'first_table_money' }],
          },
        ],
      },
      {
        id: 'dante',
        speaker: 'Dante',
        text: 'Good. Now give me a name and a door.',
      },
    ],
  };
}

function playLieutenantDialogue(): void {
  dialogue.start(lieutenantDialogue());
}
